import React from "react";

import { useTranslation } from "react-i18next";

import { Progress } from "@/src/components/ui/progress";
import { cn } from "@/src/lib/utils";

import { getQuotaUsage } from "./usage.utils";
import { formatShare } from "../charts/charts.utils";
import StatCard from "../kpi/StatCard";

import type { JSX } from "react";

import type { QueryStatus } from "../kpi/StatCard";

interface QuotaCardProps {
  label: string;
  used: number;
  max: number;
  formatAmount: (value: number) => string;
  maxCaptionKey: "usage.quota.ofMax" | "usage.quota.ofContract";
  query: QueryStatus;
}

function QuotaCard({
  label,
  used,
  max,
  formatAmount,
  maxCaptionKey,
  query,
}: QuotaCardProps): JSX.Element {
  const { t, i18n } = useTranslation("editor-analytics");
  const lang = i18n.language;
  const usage = getQuotaUsage(used, max);

  const footer = (
    <div className="flex w-full flex-col gap-2">
      <p>
        {usage
          ? t(maxCaptionKey, { max: formatAmount(max) })
          : t("usage.quota.none")}
      </p>
      {usage ? (
        <React.Fragment>
          <div className="flex items-center gap-3">
            <Progress
              aria-label={label}
              value={Math.min(usage.ratio, 1) * 100}
              className={cn(
                usage.isCritical &&
                  "*:data-[slot=progress-indicator]:bg-status-error",
              )}
            />
            <span className="text-foreground font-medium tabular-nums">
              {formatShare(usage.ratio, lang)}
            </span>
          </div>
          <p className={cn(usage.overage > 0 && "text-status-error")}>
            {usage.overage > 0
              ? t("usage.quota.overage", {
                  amount: formatAmount(usage.overage),
                })
              : t("usage.quota.remaining", {
                  amount: formatAmount(usage.remaining),
                })}
          </p>
        </React.Fragment>
      ) : null}
    </div>
  );

  return (
    <StatCard
      footer={query.isLoading ? undefined : footer}
      label={label}
      query={query}
      value={formatAmount(used)}
    />
  );
}

export default QuotaCard;
